import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Youtube, Copy, Check, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { schedulesApi, queryKeys, type AgentCommandStatus } from '@/lib/api';
import type { AgentHostStatus } from '@/pages/SchedulesPage';
import AdHocStreamModal from '@/components/AdHocStreamModal';
import { StageBadge, StageChecklist } from '@/components/streamProgress';

const watchUrl = (broadcastId: string) => `https://www.youtube.com/watch?v=${broadcastId}`;

// Compact button classes matching the on-host agent panel's palette (purple start,
// red stop) so the two surfaces look the same.
const BTN_START_SM = 'inline-flex items-center rounded-md bg-purple-700 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-purple-600 disabled:opacity-50';
const BTN_STOP_SM = 'inline-flex items-center rounded-md bg-red-700 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-red-600 disabled:opacity-50';

// Copy-to-clipboard button for a live stream's shareable link.
function CopyLinkButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
      }}
      className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
      title="Copy the shareable link"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-600 dark:text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : 'Copy link'}
    </button>
  );
}

const stateLabel: Record<string, { label: string; cls: string }> = {
  IDLE:             { label: 'Idle',       cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
  FETCHING:         { label: 'Starting',   cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  VALIDATING:       { label: 'Starting',   cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  UPDATING_YOUTUBE: { label: 'Starting',   cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  PREPARING_OBS:    { label: 'Starting',   cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' },
  STARTING_STREAM:  { label: 'Starting',   cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' },
  STREAMING:        { label: 'Live',       cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  STOPPING:         { label: 'Stopping',   cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' },
  ERROR:            { label: 'Error',      cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
};

export default function AdHocStreamsTab({ facilityId, hosts, isLoading, facilityTimezone, broadcastModeActive }: {
  facilityId: string | null;
  hosts: AgentHostStatus[];
  isLoading: boolean;
  facilityTimezone?: string;
  broadcastModeActive?: boolean;
}) {
  const qc = useQueryClient();
  const effectiveFacility = facilityId || '';
  const [adhocSheet, setAdhocSheet] = useState<string | null>(null);
  const [busySheet, setBusySheet] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (sheet: string) => setExpanded(s => { const n = new Set(s); n.has(sheet) ? n.delete(sheet) : n.add(sheet); return n; });
  const refresh = () => qc.invalidateQueries({ queryKey: queryKeys.schedules.agentStatus(effectiveFacility) });

  const stopStream = useMutation({
    mutationFn: (sheetName: string) => schedulesApi.sendAgentCommand({ facilityId: effectiveFacility, type: 'stop', sheetName }),
    onSettled: () => { setBusySheet(null); refresh(); },
  });

  // Poll the command queue fast while any start/stop is in flight — this is what
  // carries the real-time startup stages (queued → creating broadcast → … → live)
  // the agent reports via /commands/:id/progress. Same feed Event Mode uses.
  const commandsQuery = useQuery({
    queryKey: queryKeys.schedules.commands(effectiveFacility),
    queryFn: () => schedulesApi.getAgentCommands(effectiveFacility),
    enabled: !!effectiveFacility,
    refetchInterval: (q) => {
      const cmds = (q.state.data?.commands ?? []) as AgentCommandStatus[];
      const active = cmds.some(c => ['pending', 'delivered', 'running'].includes(c.status));
      return active ? 3_000 : 15_000;
    },
  });

  // Latest in-flight/most-recent start_adhoc command per sheet (drives stage + checklist).
  const startCmdBySheet = useMemo(() => {
    const m = new Map<string, AgentCommandStatus>();
    for (const c of commandsQuery.data?.commands ?? []) {
      if (c.type !== 'start_adhoc') continue;
      const prev = m.get(c.sheetName);
      if (!prev || new Date(c.createdAt).getTime() >= new Date(prev.createdAt).getTime()) m.set(c.sheetName, c);
    }
    return m;
  }, [commandsQuery.data]);

  // When any start command finishes, pull fresh agent status so the STREAMING
  // state + shareable link appear promptly (rather than waiting for the 30s poll).
  // Per command, not "all idle": in a multi-sheet batch the first sheets to go
  // live used to wait for the slowest one.
  const activeIds = useRef<Set<number>>(new Set());
  useEffect(() => {
    const cmds = (commandsQuery.data?.commands ?? []) as AgentCommandStatus[];
    const now = new Set(cmds.filter(c => ['pending', 'delivered', 'running'].includes(c.status)).map(c => c.id));
    const finished = [...activeIds.current].some(id => !now.has(id));
    activeIds.current = now;
    if (finished) refresh();
  }, [commandsQuery.data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!effectiveFacility) return <p className="text-sm text-gray-500 dark:text-gray-400">Select a facility to manage ad-hoc streams.</p>;
  if (isLoading) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-6">
      {adhocSheet && (
        <AdHocStreamModal
          facilityId={effectiveFacility}
          sheetName={adhocSheet}
          onClose={() => setAdhocSheet(null)}
          onStarted={() => { setBusySheet(adhocSheet); refresh(); qc.invalidateQueries({ queryKey: queryKeys.schedules.commands(effectiveFacility) }); }}
        />
      )}

      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700 dark:border-blue-800/50 dark:bg-blue-950/30 dark:text-blue-300">
        Start an <strong>unscheduled</strong> stream on any idle sheet — for practices, scrimmages, or one-offs outside the league schedule.
        Tick <strong>Private Stream</strong> to keep it off the public channel (unlisted). Watch each startup step live, then copy the link to share once it's on air.
      </div>

      {hosts.length === 0 && (
        <p className="text-sm italic text-gray-500 dark:text-gray-400">No agent hosts configured for this facility.</p>
      )}

      {hosts.map((h) => (
        <Card key={h.host.id}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <span className={`h-2.5 w-2.5 rounded-full ${h.isOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
              {h.host.hostLabel}
              <span className="ml-1 text-sm font-normal text-gray-500 dark:text-gray-400">{h.isOnline ? 'Online' : 'Offline'}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {h.sessions.length === 0 ? (
              <p className="text-sm italic text-gray-400">No sheets reported in the last heartbeat.</p>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {h.sessions.map((sess) => {
                  const sd = stateLabel[sess.state] ?? { label: sess.state, cls: 'bg-gray-100 text-gray-600' };
                  const streaming = sess.state === 'STREAMING';
                  const idle = sess.state === 'IDLE' || sess.state === 'ERROR';
                  const rowBusy = busySheet === sess.sheetName;
                  const url = sess.broadcastId ? watchUrl(sess.broadcastId) : null;
                  const startCmd = startCmdBySheet.get(sess.sheetName);
                  const inflight = !!startCmd && ['pending', 'delivered', 'running'].includes(startCmd.status);
                  const hasSteps = (startCmd?.stageHistory?.length ?? 0) > 0 || startCmd?.status === 'failed';
                  const isOpen = expanded.has(sess.sheetName);
                  return (
                    <div key={sess.sheetName} className="py-2.5">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="font-medium text-gray-800 dark:text-gray-200">{sess.sheetName}</span>
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${sd.cls}`}>
                          {streaming && <span className="mr-1 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-green-500" />}
                          {sd.label}
                        </span>
                        {/* Live startup stage (near-real-time via the command feed) */}
                        <StageBadge cmd={inflight || startCmd?.status === 'failed' ? startCmd : undefined} />
                        {sess.startedRemotelyBy && (
                          <span className="inline-flex items-center rounded-full bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" title={sess.startedRemotelyBy === 'curlops' ? 'Started from CurlOps' : 'Started from the CSM website'}>
                            {sess.startedRemotelyBy === 'curlops' ? 'CurlOps' : `web · ${sess.startedRemotelyBy === 'admin' ? 'Support' : 'Club Mgr'}`}
                          </span>
                        )}

                        {/* Live link (works for unlisted/private too) */}
                        {streaming && url && (
                          <div className="flex min-w-0 flex-1 items-center gap-2">
                            <Youtube className="h-4 w-4 shrink-0 text-red-500" />
                            <a href={url} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-xs text-blue-600 hover:underline dark:text-blue-400" title={url}>{url}</a>
                            <CopyLinkButton url={url} />
                            <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800" title="Open on YouTube">
                              <ExternalLink className="h-3.5 w-3.5" /> Open
                            </a>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="ml-auto flex items-center gap-1">
                          {hasSteps && (
                            <button onClick={() => toggle(sess.sheetName)} className="rounded px-1.5 py-1 text-xs text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800">
                              {isOpen ? '▾' : '▸'} steps
                            </button>
                          )}
                          {streaming && sess.curlOpsControlled ? (
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-300" title="This game stops from Finished in CurlOps. To override, use its Stop on the Event Streams tab.">
                              Controlled by CurlOps
                            </span>
                          ) : streaming ? (
                            <button className={BTN_STOP_SM} onClick={() => { setBusySheet(sess.sheetName); stopStream.mutate(sess.sheetName); }} disabled={!h.isOnline || rowBusy || broadcastModeActive} title={broadcastModeActive ? 'Disabled — Broadcast Mode is on' : !h.isOnline ? 'Host offline' : 'Stop this stream'}>
                              {rowBusy ? '…' : '⏹ Stop'}
                            </button>
                          ) : idle ? (
                            <button className={BTN_START_SM} onClick={() => setAdhocSheet(sess.sheetName)} disabled={!h.isOnline || inflight || broadcastModeActive} title={broadcastModeActive ? 'Disabled — Broadcast Mode is on' : !h.isOnline ? 'Host offline' : 'Start an unscheduled (ad-hoc) stream on this sheet'}>
                              ▶ Start ad-hoc
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">working…</span>
                          )}
                        </div>
                      </div>

                      {isOpen && hasSteps && (
                        <div className="mt-2 rounded border border-gray-100 dark:border-gray-800">
                          <StageChecklist cmd={startCmd} {...(facilityTimezone ? { tz: facilityTimezone } : {})} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
